import type { Room } from '@studio/models/room';
import { STR_NULL } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type {
  AutoLinkError,
  AutoLinkState
} from '#~/routes/verify-auto-link/v2/auto-link-types.js';
import type { BootError } from '#~/depot/types.js';
import { awaitOrganizationVisibility } from '#~/auth/registry-completeness.js';

/**
 * ENG-4154 duplicate-room fix: true while the auto-link controller may still
 * produce (or adopt) a room. `shouldBootstrapDefaultElectronRoom` must not
 * fire while this holds — it only sees the completion record + a terminal
 * `success`, so a mid-flight controller looks identical to "no auto-link"
 * and a fresh boot would create a second user-named room next to the
 * device room. `idle` counts as pending only when auto-link is expected to
 * start (iris Electron, no linked agent): React runs child effects before
 * parent effects, so the bootstrap generator can observe the controller
 * before the guard's start effect fires.
 */
export const shouldAwaitAutoLinkSettle = (
  status: AutoLinkState['status'] | undefined,
  autoLinkExpected: boolean,
  reason?: AutoLinkError
): boolean => {
  // An actively running controller is always worth awaiting, regardless of
  // how this boot's eligibility was computed.
  if (status === 'checking' || status === 'linking' || status === 'verifying_link') {
    return true;
  }

  // A cleared stale cross-account link is a reboot in flight, not a verdict:
  // the sidecar relinks after the relaunch and the next boot's controller
  // produces the room. Bootstrapping a default room now (the identity flip
  // to unlinked re-runs this generator without the cross-account flag) minted
  // a user-named room seconds before the reboot; the relaunch then landed in
  // it while the bridge linked a second room. Keep waiting — the guard's
  // timed release (`skipped`) is the exit when the reboot never lands.
  if (status === 'error' && reason === 'stale_link_cleared') {
    return true;
  }

  return status === 'idle' && autoLinkExpected;
};

/**
 * Is the controller actively producing a room on THIS boot? Unlike
 * `shouldAwaitAutoLinkSettle`, `idle` never counts: a bypassed guard leaves
 * the controller idle forever and this must not stall a boot that already has
 * rooms. Used to hold room selection when the account already has rooms —
 * otherwise a returning account on a new machine is dropped into an old room
 * while the bridge links a new one, and the new studio only shows after a
 * restart.
 */
export const isAutoLinkActive = (state: AutoLinkState | undefined): boolean =>
  state !== undefined &&
  state.status !== 'idle' &&
  shouldAwaitAutoLinkSettle(
    state.status,
    false,
    state.status === 'error' ? state.reason : undefined
  );

export interface AutoLinkRoomHoldOptions {
  /** Rooms visible to the account at the boot's list read. */
  readonly roomCount: number;
  /** Room already resolved for this boot (linked-room check / prior settle). */
  readonly recentAutoLinkRoomId: Room['id'] | undefined;
  /** Depot mirror of the controller state; undefined when no controller exists. */
  readonly state: AutoLinkState | undefined;
  /** Hold ceiling when rooms exist — an old room is a usable fallback. */
  readonly activeRoomHoldMs: number;
  /** Hold ceiling when no rooms exist — nothing to fall back to. */
  readonly settleTimeoutMs: number;
}

export interface AutoLinkRoomHold {
  /** Whether room selection must wait on the controller before proceeding. */
  readonly hold: boolean;
  /** Ceiling for that wait; only meaningful when `hold` is true. */
  readonly timeoutMs: number;
}

/**
 * Decide whether root bootstrap must hold room selection on the
 * auto-link controller, and for how long. Hold with no known room when either
 * the account has no rooms (bootstrap would otherwise mint a default room while
 * the controller is mid-flight or self-healing a stale link) or rooms exist but
 * the controller is actively linking THIS machine (choosing from the stale list
 * drops the user into a room the bridge never joins). Rooms-exist holds use the
 * shorter ceiling so a wedged link never hides a usable room for minutes. Pure
 * so the hook-level decision is unit-testable while the generator harness is
 * skipped (use-root-bootstrap.integration.test.ts TODO(test-rewrite)).
 */
export const resolveAutoLinkRoomHold = ({
  roomCount,
  recentAutoLinkRoomId,
  state,
  activeRoomHoldMs,
  settleTimeoutMs
}: AutoLinkRoomHoldOptions): AutoLinkRoomHold => {
  const hasRooms = roomCount > 0;

  return {
    hold: recentAutoLinkRoomId === undefined && (!hasRooms || isAutoLinkActive(state)),
    timeoutMs: hasRooms ? activeRoomHoldMs : settleTimeoutMs
  };
};

export interface AutoLinkSettleOptions {
  /** Live view of the controller's state; undefined when no controller exists. */
  readonly peekState: () => AutoLinkState | undefined;
  /** Whether auto-link is expected to start on this boot (gates `idle`). */
  readonly autoLinkExpected: boolean;
  /** Liveness backstop — the guard's own ceiling should fire well before it. */
  readonly timeoutMs: number;
  readonly pollIntervalMs: number;
  readonly signal?: AbortSignal;
}

/**
 * Poll until the auto-link controller settles (terminal state), the deadline
 * passes, or the boot is aborted. Returns the final observed state so the
 * caller can adopt the controller's room on `success`. Extracted from
 * `use-root-bootstrap` so the race fix is unit-testable — the hook-level
 * integration harness is skipped (see use-root-bootstrap.integration.test.ts
 * TODO(test-rewrite)).
 */
export const awaitAutoLinkSettle = async ({
  peekState,
  autoLinkExpected,
  timeoutMs,
  pollIntervalMs,
  signal
}: AutoLinkSettleOptions): Promise<AutoLinkState | undefined> => {
  const deadline = Date.now() + timeoutMs;

  while (!signal?.aborted) {
    const state = peekState();
    // Reason-aware: a cleared stale cross-account link keeps the wait alive
    // (see shouldAwaitAutoLinkSettle), and the settled state is returned as
    // observed rather than re-peeked after the loop.
    if (
      !shouldAwaitAutoLinkSettle(
        state?.status,
        autoLinkExpected,
        state?.status === 'error' ? state.reason : undefined
      )
    ) {
      return state;
    }

    if (Date.now() >= deadline) {
      return state;
    }

    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  return peekState();
};

export interface BootstrapRoomAdoptionOptions<R> {
  /** Hex id returned by ensureDefaultOrgAndRoom's create path. */
  createdRoomId: string;
  /** Rooms list as of the post-create re-list. */
  rooms: R[];
  listRooms: () => Promise<R[]>;
  fetchRoomById: (roomId: string) => Promise<R | undefined>;
  roomIdOf: (room: R) => string | undefined;
  timeoutMs: number;
  pollIntervalMs: number;
  signal?: AbortSignal;
}

/**
 * Adopt the bootstrap-created default room even when listRooms lags the
 * create. QA (Hive ITG 2026-08-10, replay 019fecc4…): a new account's boot
 * CREATED its default room, but the immediate post-create listRooms — served
 * by a read replica — did not include it yet, so the boot fell through to
 * `throw 'No rooms are available for this account.'` → "Unable to load
 * account. Please try again." (retry always worked because the room existed
 * by then). Iris never hit this because the AUTO-LINK path has a sync-poll +
 * fetchRoomById direct-verify; the default-room bootstrap — the only path on
 * no-auto-link products (Hive) — had neither. This helper mirrors that proven
 * pattern for the bootstrap path: direct lookup first (deterministic, no wait
 * in the common case), then a bounded list poll; if the room still cannot be
 * observed, the caller's existing no-rooms handling decides. Extracted like
 * `awaitAutoLinkSettle` so the race fix is unit-testable.
 * Per rules: settle-every-terminal-branch, reference-rules-in-comments
 * (ai.knowledge/global) — every exit returns a usable rooms list.
 */
export const awaitBootstrapRoomAdoption = async <R>({
  createdRoomId,
  rooms,
  listRooms,
  fetchRoomById,
  roomIdOf,
  timeoutMs,
  pollIntervalMs,
  signal
}: BootstrapRoomAdoptionOptions<R>): Promise<R[]> => {
  const contains = (list: R[]): boolean => list.some((room) => roomIdOf(room) === createdRoomId);

  if (contains(rooms)) {
    return rooms;
  }

  // Direct point-read: usually lands even while the list read lags.
  try {
    const created = await fetchRoomById(createdRoomId);

    if (created) {
      return [...rooms, created];
    }
  } catch {
    // Registry weather on the point-read — the poll below is the backstop.
  }

  const deadline = Date.now() + timeoutMs;
  let current = rooms;

  while (Date.now() < deadline && !signal?.aborted) {
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

    if (signal?.aborted) {
      return current;
    }

    try {
      current = await listRooms();

      if (contains(current)) {
        return current;
      }
    } catch {
      // Transient list failure: keep polling until the deadline.
    }
  }

  return current;
};

export interface BootstrapOrgVisibilityOptions<O> {
  /** Hex organizationId of the adopted bootstrap room. */
  createdOrgId: string;
  /** Organizations list as of the boot's org load. */
  organizations: O[];
  listOrganizations: () => Promise<O[]>;
  orgIdOf: (organization: O) => string | undefined;
  timeoutMs: number;
  pollIntervalMs: number;
  signal?: AbortSignal;
}

/**
 * Org-side sibling of {@link awaitBootstrapRoomAdoption} — same replica-lag
 * family, third member. QA (Hive ITG 2026-08-10): with the room adoption in
 * place the boot proceeds, but the ORGANIZATION created by the same
 * ensureDefaultOrgAndRoom call can still be missing from the boot's
 * listOrganizations read — and the header switcher labels the studio
 * "Create a studio" until orgs contain the room's organizationId (that exact
 * causal chain is documented at the linkedAgentID write in
 * use-root-bootstrap: "empty orgs → 'Create a studio'"). A reload always
 * self-healed. Bounded list poll only (no client org-get API exists); at the
 * deadline the last observed list is returned and the cosmetic fallback
 * label degrades gracefully — never a boot failure.
 * Per rules: settle-every-terminal-branch, reference-rules-in-comments
 * (ai.knowledge/global) — every exit returns a usable organizations list.
 */
export const awaitBootstrapOrgVisibility = <O>({
  createdOrgId,
  organizations,
  listOrganizations,
  orgIdOf,
  timeoutMs,
  pollIntervalMs,
  signal
}: BootstrapOrgVisibilityOptions<O>): Promise<O[]> =>
  awaitOrganizationVisibility({
    organizationIds: [createdOrgId],
    organizations,
    listOrganizations,
    orgIdOf,
    timeoutMs,
    pollIntervalMs,
    ...(signal ? { signal } : {})
  });

/**
 * Default studio name for the Electron bootstrap room. ENG-4154: a literal
 * 'null' token (the STR_NULL fallback, persisted into older accounts' names)
 * must never reach a visible room name — "<firstName> null" studios.
 */
export const deriveDefaultStudioName = (
  user: T_Maybe<{
    firstName?: T_Maybe<string>;
    lastName?: T_Maybe<string>;
    email?: T_Maybe<string>;
  }>
): string => {
  const nameParts = [user?.firstName, user?.lastName].filter(
    (part): part is string => !!part && part !== STR_NULL
  );

  return nameParts.length > 0 ? nameParts.join(' ') : (user?.email ?? 'My Studio');
};

export const shouldWaitForAutoLinkRoomSync = (
  roomIds: ReadonlySet<Room['id']>,
  recentAutoLinkRoomId: Room['id'] | undefined
): boolean => {
  return recentAutoLinkRoomId !== undefined && !roomIds.has(recentAutoLinkRoomId);
};

export const hasAutoLinkRoomSynced = (
  roomIds: ReadonlySet<Room['id']>,
  recentAutoLinkRoomId: Room['id'] | undefined
): boolean => {
  if (recentAutoLinkRoomId === undefined) {
    return true;
  }

  return roomIds.has(recentAutoLinkRoomId);
};

export const shouldBootstrapDefaultElectronRoom = (
  roomIds: ReadonlySet<Room['id']>,
  recentAutoLinkRoomId: Room['id'] | undefined,
  /**
   * ENG-4154: agents/get returned an in-band 403 for the local bridge — it is
   * linked under an org this account cannot access. Creating a fallback room in
   * this boot would manufacture a guaranteed duplicate, so suppress the
   * default-room bootstrap. ENG-4166 D1: this is now a TRANSIENT signal that the
   * boot re-verifies after the auto-link controller settles — an accessible
   * settled room is adopted (bootstrap stays suppressed because
   * recentAutoLinkRoomId is set), and an unrecoverable forbidden hands off to
   * the boot-error screen (see shouldHandOffToCrossAccountBootError) — never a
   * boot-scoped latch that suppresses forever with no exit.
   */
  crossAccountLinkDetected = false
): boolean => {
  return roomIds.size === 0 && recentAutoLinkRoomId === undefined && !crossAccountLinkDetected;
};

// ENG-4166 D3: distinct boot-error copy for a cold boot whose bridge is linked
// to a studio the current account cannot access and could not be recovered in
// place. Surfaced on the existing boot-error screen (depot.boot.error →
// BootErrorScreen) instead of an indefinite "Loading your studio…" spinner.
export const CROSS_ACCOUNT_BOOT_ERROR_MESSAGE =
  'This device is linked to a studio your account can’t open.';
export const CROSS_ACCOUNT_BOOT_ERROR_DETAIL =
  'Sign in with the account that owns this studio, or re-link the device to your account, then try again.';

/**
 * ENG-4166 D3: build the retryable BootError the doomed cross-account cold boot
 * routes to. Retry clears the error and reloads — a fresh boot re-resolves the
 * link (and, if the owning account has since signed in, proceeds). Pure so the
 * decision + copy are unit-testable without a renderer; the caller injects
 * `onRetry` (reload) and `onSignOut` (Electron logout) so this stays free of
 * `window`. `onSignOut` exists because retry cannot help a signed-in account
 * that does not own the linked studio — without it this screen trapped the
 * user with `database.db` deletion as the only escape (QA, Hive ITG
 * 2026-08-10).
 */
export const makeCrossAccountBootError = (
  onRetry: () => void,
  onSignOut?: () => void
): BootError => ({
  message: CROSS_ACCOUNT_BOOT_ERROR_MESSAGE,
  detail: CROSS_ACCOUNT_BOOT_ERROR_DETAIL,
  severity: 'retryable',
  retryFn: onRetry,
  signOutFn: onSignOut ?? null
});

/**
 * ENG-4166 D1: once the auto-link controller has settled, decide whether a boot
 * that saw a forbidden (cross-account) linked-room check must hand off to the
 * boot-error screen. The old code latched the one-shot 403 for the whole boot
 * and suppressed default-room bootstrap with NO exit — the boot then waited
 * forever on a rooms list that never gained a visible room. Now: when an
 * accessible room was resolved (`resolvedRoomId` defined — the controller
 * settled `success` on a reachable room, or a real room synced in) the boot
 * PROCEEDS; only when forbidden was seen AND no reachable room emerged do we
 * hand off to the bounded boot-error exit (D3).
 */
export const shouldHandOffToCrossAccountBootError = (
  crossAccountLinkDetected: boolean,
  resolvedRoomId: Room['id'] | undefined
): boolean => crossAccountLinkDetected && resolvedRoomId === undefined;

/**
 * ENG-4037 workspace-entry gate: whether the boot may honor the lastRoomId
 * fast-path. Honored when the user checked remember-my-choice on the picker,
 * or trivially when the account owns at most one studio (nothing to pick).
 * Pure + exported so the picker-routing decision is unit-testable without the
 * bootstrap harness (mirrors shouldAwaitAutoLinkSettle above).
 */
export const shouldHonorLastRoomId = (rememberStudio: boolean, roomCount: number): boolean =>
  rememberStudio || roomCount <= 1;

/**
 * ENG-4037 workspace-entry decision, applied AFTER the auto-link/remembered
 * resolution: with no room decided and several studios available, the user
 * picks on /select-studio instead of being silently dropped into the first
 * room (the pre-ENG-4037 behavior).
 */
export const shouldShowWorkspacePicker = (
  resolvedRoomId: T_Maybe<Room['id']>,
  roomCount: number
): boolean => resolvedRoomId === undefined && roomCount > 1;
